/**
 * Price provenance — WHERE a balance's fiat value came from, and HOW OLD it is.
 *
 * WHY THIS EXISTS
 *
 * A Balances row prints a fiat value for every priced holding and, until this
 * contract, said nothing about the quote behind it. Every asset is valued at its
 * last daily close ON OR BEFORE the requested date, and that lookup has NO lower
 * bound (`CandleRepository.findDailyOnOrBefore`): when the market-data pipeline
 * stops covering an asset, the row keeps printing a confident number computed
 * from a close that can be over a year old. On the e2e fixture ADA and LINK price
 * off 2025-06-15 closes while ETH prices off 2026-08-03 — and both render
 * identically.
 *
 * The producing service already knows the answer (`MultiCurrencyRatesService`
 * builds `provider` and `lastUpdated` from the candle row) and threw it away at
 * `BalanceFormatter.calculateFiatValue`'s `Promise<string | null>` return type.
 * This is the object that carries it to the screen.
 *
 * SCOPE — THE ASSET PRICE LEG ONLY (deliberate, see the report in #1113)
 *
 * A crypto→EUR valuation multiplies TWO quotes: the asset's USD close (from
 * `bat.candles`) and a USD→EUR rate (from `bat.fx_rates`). `source`/`quotedAt`
 * describe the ASSET leg — the one the market-data download (#1110) refreshes and
 * the one the configured source priority (#1112) chooses. The FX leg has its own,
 * independent freshness and is NOT folded in: the two ages answer different
 * questions, and collapsing them into one number would make the flag fire on
 * every row of a EUR portfolio whenever the FX feed lags, which is exactly the
 * useless-signal failure this disclosure has to avoid.
 */

/**
 * Whole days of age above which a quote is disclosed as STALE.
 *
 * WHY SEVEN, AND NOT "OLD"
 *
 * Crypto candles are daily and the market trades 24/7, so a fully-current asset
 * is at most ONE day behind (today's candle has not closed yet). The threshold
 * therefore only has to clear the gaps a HEALTHY pipeline can legitimately
 * produce — a download that runs on a weekly-ish cadence, a provider outage of a
 * couple of days, a bank-holiday run of missing days on the fiat side. Seven days
 * is the smallest whole week that no normal market-calendar gap can reach, so the
 * flag means "nothing has priced this asset in over a week", never "it is
 * Sunday". Anything shorter (1–3 days) fires on routine cadence and trains the
 * user to ignore it; anything longer stops being a warning — a month-old close
 * has already moved the value by tens of percent.
 */
export const STALE_PRICE_THRESHOLD_DAYS = 7

/**
 * The TIMELESS half of a quote's provenance: which row was read, and for which
 * day. Both facts are immutable once the lookup happened, which is what makes
 * this half safe to put in the rate cache — unlike the age below, it cannot go
 * out of date while it sits in Redis.
 *
 * ⚠️ NOT balances-only, despite the file it lives in. The transactions list and
 * its CSV/XLSX/PDF export carry this same object per valued leg
 * (`TransactionLegResponse.marketValueQuote`) and print it inside the value cell.
 * They deliberately take THIS half and not `PriceProvenance` below: their asset
 * lookup is bounded to the transaction's own day, so `ageInDays`/`isStale` would
 * be a constant 0 there and an indicator that can never fire is worse than none.
 */
export interface PriceQuoteOrigin {
  /**
   * The market-data source that quoted the price — the `candles.source` value
   * (`binance`, `kraken`, `coingecko`, `synthetic`, …) for a crypto holding, or
   * the `fx_rates.source` (`ecb`, `frankfurter`, …) for a fiat→fiat conversion.
   * Never a fabricated provider name: it is the row that was actually read.
   */
  source: string

  /**
   * Calendar day of the quote actually used, `YYYY-MM-DD`. This is the day of
   * the CLOSE, not the moment the page was fetched — the two can be a year apart.
   */
  quotedAt: string

  /**
   * ⚠️⚠️ EL SÍMBOLO QUE DE VERDAD PUSO EL PRECIO, cuando NO es el del saldo.
   *
   * El buscador de cotizaciones tiene un último recurso por alias
   * (`multi-currency-rates.service.ts`): un saldo guardado bajo un código
   * heredado de exchange —`XBT`, `XDG`— se valora con la vela de la moneda que
   * el catálogo nombra sin ambigüedad. Es DELIBERADO y sólo puede rellenar una
   * valoración ausente, nunca re-apuntar una existente.
   *
   * Lo que faltaba era decirlo. MEDIDO POR LA UI el 2026-08-18 en `/balances`,
   * fila `CSV Upload · XBT`:
   *
   *     cantidad   -0,01005000 XBT
   *     valor      -652,17 USD          ← 64.892,54 USD/unidad: el precio de BTC
   *     tooltip    «Cotización de Binance del 27/07/2026 · 22 días…»
   *
   * Idéntico al de una fila resuelta (SOL/Solana). En el mismo medio, los otros
   * tickers sin resolver —NEAR, QQRS, WWTT, ZZQX— dan «N/D», así que XBT es el
   * único con precio y nada explica de dónde sale. El importe entra en el TOTAL
   * y queda FUERA del aviso de «activos sin precio de mercado».
   *
   * Ausente cuando el precio salió del propio símbolo, que es el caso normal:
   * un valor siempre presente obligaría a comparar cadenas para saber si hubo
   * alias, y la mitad de las superficies lo imprimiría de más.
   */
  pricedAsSymbol?: string
}

/**
 * Provenance of the market quote that priced ONE balance row.
 *
 * A provenance OBJECT rather than three loose optional fields on the balance:
 * every layer between the candle row and the table cell (formatter → calculator →
 * aggregator → DTO → UI type) then carries one named thing, so adding a fourth
 * fact later costs one field here instead of five signature changes.
 */
export interface PriceProvenance extends PriceQuoteOrigin {
  /**
   * Whole calendar days between `quotedAt` and the date the balance was valued
   * FOR. Never negative: a quote can never post-date its own valuation date, and
   * clamping at 0 keeps a clock skew from rendering as "-1 días".
   */
  ageInDays: number

  /** `ageInDays > STALE_PRICE_THRESHOLD_DAYS`. Precomputed so every surface agrees. */
  isStale: boolean
}

/** Milliseconds in one day — the divisor for the UTC calendar-day difference. */
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Whole calendar days from `quotedAt` to `valuedOn`, both `YYYY-MM-DD`.
 *
 * Both ends are read as UTC midnight, so the answer is a difference of CALENDAR
 * DAYS and never depends on the server's timezone — `Date.parse` on a bare
 * `YYYY-MM-DD` is defined to be UTC. Clamped at 0 (see `ageInDays`). Returns 0
 * for an unparseable input rather than `NaN`, so a malformed date can never
 * render as "NaN días" or trip the stale comparison in an undefined direction.
 */
export function computeQuoteAgeInDays(quotedAt: string, valuedOn: string): number {
  const quotedMs = Date.parse(`${quotedAt.slice(0, 10)}T00:00:00Z`)
  const valuedMs = Date.parse(`${valuedOn.slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(quotedMs) || Number.isNaN(valuedMs)) {
    return 0
  }
  return Math.max(0, Math.round((valuedMs - quotedMs) / MILLISECONDS_PER_DAY))
}

/**
 * Build the provenance a row will disclose.
 *
 * `valuedOn` is the date the BALANCE was requested for — today for the live view,
 * the chosen day for an "as of" snapshot. Measuring against the requested date
 * (not `now`) is what keeps a 2023 snapshot from flagging every one of its
 * correctly-2023 quotes as stale.
 */
/**
 * A price-source SLUG turned into something a user can read.
 *
 * The stored value is a technical identifier — `binance`, `ecb`, `ecb-cross`,
 * `synthetic` — and printing it raw is the "stored slug on screen" defect this
 * codebase has already closed on seven other surfaces. `formatPlatformDisplay`
 * is not enough on its own: it only splits underscores and title-cases, which
 * turns `ecb` into "Ecb" and `ecb-cross` into "Ecb-cross".
 *
 * The rule, deliberately a RULE and not a lookup map (a map goes stale the day
 * an admin registers a new source): split on any separator, then upper-case
 * tokens of three characters or fewer — real acronyms in this vocabulary are all
 * short (`ecb`, `okx`, `bts`) and real names are all longer (`binance`,
 * `kraken`, `coingecko`, `frankfurter`).
 *
 *   `binance`   → "Binance"      `ecb`        → "ECB"
 *   `ecb-cross` → "ECB Cross"    `synthetic`  → "Synthetic"
 */
export function formatPriceSourceLabel(source: string): string {
  return source
    .split(/[\s_\-+.]+/)
    .filter((token) => token.length > 0)
    .map((token) =>
      token.length <= 3
        ? token.toUpperCase()
        : token.charAt(0).toUpperCase() + token.slice(1).toLowerCase(),
    )
    .join(' ')
}

export function buildPriceProvenance(origin: PriceQuoteOrigin, valuedOn: string): PriceProvenance {
  const ageInDays = computeQuoteAgeInDays(origin.quotedAt, valuedOn)
  return {
    source: origin.source,
    quotedAt: origin.quotedAt.slice(0, 10),
    ageInDays,
    isStale: ageInDays > STALE_PRICE_THRESHOLD_DAYS,
    // Esta función ELIGE campo a campo en vez de expandir `origin`, así que un
    // dato nuevo que no se nombre aquí se pierde en silencio entre la vela y la
    // celda. Condicional porque `exactOptionalPropertyTypes` distingue «ausente»
    // de «presente y undefined», y las dos superficies leen la ausencia.
    ...(origin.pricedAsSymbol !== undefined ? { pricedAsSymbol: origin.pricedAsSymbol } : {}),
  }
}
